'use client'

import * as React from 'react'
import { useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { gsap } from 'gsap'
import { Draggable } from 'gsap/Draggable'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import { Button } from '@sarkiai/ui'
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react'
import Link from 'next/link'

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(Draggable)
}

interface ServiceCard {
  id: string
  title: string
  description: string
  backgroundImage: string
  borderColor: string
  href: string
}

export function SeAServicesCTA() {
  const t = useTranslations('corporate_services')
  const cardsRef = useRef<HTMLUListElement>(null)
  const dragProxyRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null)

  const seamlessLoopRef = useRef<gsap.core.Timeline | null>(null)
  const scrubRef = useRef<gsap.core.Tween | null>(null)
  const playheadRef = useRef({ offset: 0 })

  const spacing = 0.33
  const autoPlayInterval = 5000

  const services = useMemo<ServiceCard[]>(() => [
    {
      id: 'xline',
      title: t('xline.title'),
      description: t('xline.description'),
      backgroundImage: '/assets/images/services/sea/xline.webp',
      borderColor: '#1c8fad',
      href: '/enterprise/xline'
    },
    {
      id: 'xsare',
      title: t('xsare.title'),
      description: t('xsare.description'),
      backgroundImage: '/assets/images/services/sea/xsare.webp',
      borderColor: '#e74c3c', // Reddish for contrast
      href: '/enterprise/xsare'
    },
    {
      id: 'xedu',
      title: t('xedu.title'),
      description: t('xedu.description'),
      backgroundImage: '/assets/images/services/sea/xedu.webp',
      borderColor: '#2ecc71', // Greenish for contrast
      href: '/enterprise/xedu'
    }
  ], [])

  const displayCards = useMemo(() => [...services, ...services, ...services], [services])
  const scrollToOffsetRef = useRef<((offset: number) => void) | null>(null)

  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current)
    autoPlayRef.current = setInterval(() => {
      if (scrubRef.current && scrollToOffsetRef.current) {
        scrollToOffsetRef.current(scrubRef.current.vars.offset + spacing)
      }
    }, autoPlayInterval)
  }, [spacing])

  const stopAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current)
      autoPlayRef.current = null
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !cardsRef.current) return

    const cards = gsap.utils.toArray<HTMLLIElement>('.service-card')
    if (cards.length === 0) return

    gsap.set(cards, { xPercent: 400, opacity: 0, scale: 0 })
    const snapTime = gsap.utils.snap(spacing)

    const animateFunc = (element: HTMLLIElement) => {
      const tl = gsap.timeline()
      tl.fromTo(
        element,
        {
          scale: 0.5,
          opacity: 0,
          filter: 'blur(4px)',
          zIndex: 0
        },
        {
          scale: 1.2,
          opacity: 1,
          filter: 'blur(0px)',
          zIndex: 100,
          duration: 0.5,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
          immediateRender: false,
        }
      ).fromTo(
        element,
        { xPercent: 100 },
        { xPercent: -200, duration: 1, ease: 'none', immediateRender: false },
        0
      )
      return tl
    }

    const buildSeamlessLoop = (
      items: HTMLLIElement[],
      spacing: number,
      animateFunc: (element: HTMLLIElement) => gsap.core.Timeline
    ) => {
      const overlap = Math.ceil(1 / spacing)
      const startTime = items.length * spacing + 0.5
      const loopTime = (items.length + overlap) * spacing + 1
      const rawSequence = gsap.timeline({ paused: true })
      const seamlessLoop = gsap.timeline({
        paused: true,
        repeat: -1,
        onRepeat() {
          if (this._time === this._dur) {
            this._tTime += this._dur - 0.01
          }
        },
      })
      const l = items.length + overlap * 2

      for (let i = 0; i < l; i++) {
        const index = i % items.length
        const time = i * spacing
        rawSequence.add(animateFunc(items[index]), time)
        if (i <= items.length) {
          seamlessLoop.add('label' + i, time)
        }
      }

      rawSequence.time(startTime)
      seamlessLoop
        .to(rawSequence, {
          time: loopTime,
          duration: loopTime - startTime,
          ease: 'none',
        })
        .fromTo(
          rawSequence,
          { time: overlap * spacing + 1 },
          {
            time: startTime,
            duration: startTime - (overlap * spacing + 1),
            immediateRender: false,
            ease: 'none',
          }
        )
      return seamlessLoop
    }

    const seamlessLoop = buildSeamlessLoop(cards, spacing, animateFunc)
    seamlessLoopRef.current = seamlessLoop

    const wrapTime = gsap.utils.wrap(0, seamlessLoop.duration())

    const scrub = gsap.to(playheadRef.current, {
      offset: 0,
      onUpdate() {
        seamlessLoop.time(wrapTime(playheadRef.current.offset))
        const normalizedOffset = Math.abs(playheadRef.current.offset % services.length)
        const newIndex = Math.round(normalizedOffset / spacing) % services.length
        if (newIndex >= 0 && newIndex < services.length) {
          setCurrentIndex(newIndex)
        }
      },
      duration: 0.5,
      ease: 'power3',
      paused: true,
    })
    scrubRef.current = scrub

    const scrollToOffset = (offset: number) => {
      const snappedTime = snapTime(offset)
      scrub.vars.offset = snappedTime
      scrub.invalidate().restart()
    }

    scrollToOffsetRef.current = scrollToOffset

    if (dragProxyRef.current) {
      Draggable.create(dragProxyRef.current, {
        type: 'x',
        trigger: cardsRef.current,
        onPress() {
          this.startOffset = scrub.vars.offset
          stopAutoPlay()
        },
        onDrag() {
          scrub.vars.offset = this.startOffset + (this.startX - this.x) * 0.001
          scrub.invalidate().restart()
        },
        onDragEnd() {
          scrollToOffset(scrub.vars.offset)
          startAutoPlay()
        },
      })
    }

    scrub.vars.offset = spacing * 3 // Adjust to start at the first real card centered
    scrub.invalidate().restart()
    startAutoPlay()
    setIsReady(true)

    return () => {
      seamlessLoop.kill()
      scrub.kill()
      stopAutoPlay()
    }
  }, [services.length, spacing, startAutoPlay, stopAutoPlay])

  const handlePrev = useCallback(() => {
    if (scrubRef.current && scrollToOffsetRef.current) {
      scrollToOffsetRef.current(scrubRef.current.vars.offset - spacing)
      startAutoPlay()
    }
  }, [spacing, startAutoPlay])

  const handleNext = useCallback(() => {
    if (scrubRef.current && scrollToOffsetRef.current) {
      scrollToOffsetRef.current(scrubRef.current.vars.offset + spacing)
      startAutoPlay()
    }
  }, [spacing, startAutoPlay])

  return (
    <section className="relative w-full py-18 px-4 lg:px-12 overflow-hidden bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-12 space-y-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-4xl uppercase tracking-tighter"
          >
            {t('sectionTitle')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            {t('sectionSubtitle')}
          </motion.p>
        </div>

        <div className={`relative w-full h-[35vh] md:h-[40vh] transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
          <ul
            ref={cardsRef}
            className="absolute w-full h-full top-0 left-0"
          >
            {displayCards.map((service, index) => (
              <li
                key={`${service.id}-${index}`}
                className="service-card list-none p-0 m-0 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{
                  width: 'clamp(20rem, 50vw, 35rem)',
                  aspectRatio: '16/9',
                }}
              >
                <div
                  className="w-full h-full rounded-2xl bg-cover bg-no-repeat bg-center shadow-xl transition-all duration-500 hover:scale-[1.02] border border-foreground/20"
                  style={{
                    backgroundImage: `url(${service.backgroundImage})`,
                  }}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="relative w-full max-w-2xl mx-auto mt-12">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrev}
            className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border-foreground/10 hover:bg-background"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border-foreground/10 hover:bg-background"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          <div className="flex flex-col items-center gap-6 px-4 md:px-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center space-y-3"
              >
                <h3 className="text-2xl md:text-3xl uppercase tracking-tighter leading-none">
                  {services[currentIndex]?.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground max-w-md mx-auto leading-relaxed line-clamp-2">
                  {services[currentIndex]?.description}
                </p>
              </motion.div>
            </AnimatePresence>

            <Button asChild size="lg" className="rounded-2xl h-10 px-10 bg-black text-white dark:bg-white dark:text-black hover:bg-black hover:opacity-90 uppercase tracking-widest text-xs transition-all group">
              <Link href={services[currentIndex]?.href}>
                {t('learnMore')}
                <ArrowRight className="ml-2 w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
      <div ref={dragProxyRef} className="drag-proxy invisible absolute" />
    </section>
  )
}
